<template>
  <HrPageLayout
    title="Employees Absent Without Leave (AWOL)"
    subtitle="AWOL report by month and department"
    icon="⚠️"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Reports' },
      { label: 'AWOL Report' }
    ]"
  >
    <template #tabs>
      <ReportTabs />
    </template>
    <template #header-actions>
      <select v-model="selectedMonth" @change="fetchAwolData" aria-label="Select Month" class="hr-select" style="max-width: 160px;">
        <option value="">All Months</option>
        <option value="1">January</option>
        <option value="2">February</option>
        <option value="3">March</option>
        <option value="4">April</option>
        <option value="5">May</option>
        <option value="6">June</option>
        <option value="7">July</option>
        <option value="8">August</option>
        <option value="9">September</option>
        <option value="10">October</option>
        <option value="11">November</option>
        <option value="12">December</option>
      </select>
      <button class="hr-btn hr-btn-outline hr-me-2" @click="exportToPDF">
        <i class="bx bxs-file-pdf hr-me-1"></i> Export PDF
      </button>
      <button class="hr-btn hr-btn-primary" @click="downloadReport">
        <i class="bx bx-download hr-me-1"></i> Download Excel
      </button>
    </template>

    <div class="report-awol-content">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Employees</p>
          <h2 class="hr-metric-value">{{ stats.totalEmployees }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-user-x"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Absent w/o Leave</p>
          <h2 class="hr-metric-value">{{ stats.totalAwolEmployees }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-calendar-x"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Unauthorized Days Off</p>
          <h2 class="hr-metric-value">{{ stats.totalUnauthorizedDays }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Unauthorized Hours</p>
          <h2 class="hr-metric-value">{{ stats.totalUnauthorizedHours }}</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-flex hr-flex-wrap hr-gap-2 hr-align-center" style="justify-content: space-between;">
            <div class="hr-search-wrap" style="flex:1; max-width: 280px;">
              <input v-model="search" @input="filterEmployees" class="hr-input" placeholder="Search employees..." type="text">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="selectedDepartment" @change="fetchAwolData" class="hr-select" style="min-width: 200px;">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading...</p>
          </div>
          <div v-else-if="filteredEmployees.length === 0" class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-user-x"></i></div>
            <p>No employees found matching your criteria</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Days Off Without Permission</th>
                  <th>Last Seen</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="employee in filteredEmployees" :key="employee.id">
                  <td>{{ employee.employeeId }}</td>
                  <td>{{ employee.name }}</td>
                  <td>{{ employee.departmentName }}</td>
                  <td><span class="hr-badge hr-badge-danger">{{ employee.daysOff }}</span></td>
                  <td>
                    <span v-if="employee.lastSeen">{{ formatDate(employee.lastSeen) }}</span>
                    <span v-else class="hr-text-muted">Never</span>
                  </td>
                  <td>
                    <button class="hr-btn hr-btn-sm hr-btn-primary" data-bs-target="#viewDetailsModal" data-bs-toggle="modal" @click="viewEmployeeDetails(employee)">
                      View Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- View Details Modal (Bootstrap) -->
      <div aria-hidden="true" aria-labelledby="viewDetailsModalLabel" class="modal fade" id="viewDetailsModal"
           tabindex="-1">
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content shadow-lg">
            <div class="modal-header bg-light">
              <h5 class="modal-title" id="viewDetailsModalLabel">
                <i class="mdi mdi-calendar-remove me-2"></i>Days Off Without Permission - {{ selectedEmployee ? selectedEmployee.name : '' }}
              </h5>
              <button aria-label="Close" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" type="button"></button>
            </div>
            <div class="modal-body p-4">
              <div v-if="employeeAbsentDates.length === 0" class="text-center py-4">
                <div v-if="loadingDetails" class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <div v-else>No absence dates found for this employee</div>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-bordered table-striped table-hover table-sm">
                  <thead class="table-light">
                  <tr>
                    <th>No.</th>
                    <th>Date</th>
                    <th>Day of the Week</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(date, index) in employeeAbsentDates" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>{{ formatDate(date.date) }}</td>
                    <td>{{ date.dayOfWeek }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)" type="button">
                <i class="mdi mdi-close me-2"></i>Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ReportTabs from "@/components/reports/ReportTabs.vue";
import { execute, authUrl } from "@/api";
import moment from "moment/moment";
import * as XLSX from 'xlsx';
import { buildBrandedHeaderRows, generateBrandedPDF, getCompanyName } from "@/utils/reportExportUtils";

export default {
  name: "AwolReport",
  components: { HrPageLayout, ReportTabs },
  data() {
    return {
      departments: [],
      employees: [],
      filteredEmployees: [],
      selectedDepartment: "",
      selectedMonth: "",
      search: "",
      selectedEmployee: null,
      employeeAbsentDates: [],
      stats: {
        totalEmployees: 0,
        totalAwolEmployees: 0,
        totalUnauthorizedDays: 0,
        totalUnauthorizedHours: 0
      },
      loading: false,
      loadingDetails: false,
      error: null
    };
  },
  computed: {
    selectedMonthName() {
      if (!this.selectedMonth) return 'All Months';
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return months[parseInt(this.selectedMonth) - 1] || 'All Months';
    },
    selectedDepartmentName() {
      if (!this.selectedDepartment) return 'All Departments';
      const dept = this.departments.find(d => String(d.id) === String(this.selectedDepartment));
      return dept ? dept.departmentName : 'All Departments';
    },
    headerStats() {
      return [
        { icon: 'bx bx-group', label: 'Total Employees', value: this.stats.totalEmployees || 0 },
        { icon: 'bx bx-user-x', label: 'Absent w/o Leave', value: this.stats.totalAwolEmployees || 0 },
        { icon: 'bx bx-calendar-x', label: 'Unauthorized Days', value: this.stats.totalUnauthorizedDays || 0 },
        { icon: 'bx bx-time-five', label: 'Unauthorized Hours', value: this.stats.totalUnauthorizedHours || 0 }
      ];
    }
  },
  mounted() {
    this.getDepartments();
    this.fetchAwolData();
  },
  methods: {
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              if (res.data.data && Array.isArray(res.data.data.departments)) {
                this.departments = res.data.data.departments;
              } else {
                this.departments = [];
              }
            } else {
              this.error = res.data.message || "Failed to fetch departments";
            }
          })
          .catch((e) => {
            this.error = e.message || "An error occurred while fetching departments";
            console.error("Error fetching departments:", e);
          });
    },

    fetchAwolData() {
      this.loading = true;
      this.employees = [];
      this.filteredEmployees = [];

      const data = new FormData();
      data.append("function", "getAwolEmployees");

      if (this.selectedMonth) {
        data.append("month", this.selectedMonth);
      }

      if (this.selectedDepartment) {
        data.append("departmentId", this.selectedDepartment);
      }

      if (this.search) {
        data.append("search", this.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.employees = Array.isArray(res.data.data.employees) ? res.data.data.employees : [];
              this.stats = res.data.data.stats || {
                totalEmployees: 0,
                totalAwolEmployees: 0,
                totalUnauthorizedDays: 0,
                totalUnauthorizedHours: 0
              };
              this.filterEmployees();
            } else {
              this.error = res.data.message || "Failed to fetch AWOL data";
              this.employees = [];
              this.filteredEmployees = [];
              this.stats = {
                totalEmployees: 0,
                totalAwolEmployees: 0,
                totalUnauthorizedDays: 0,
                totalUnauthorizedHours: 0
              };
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message || "An error occurred while fetching AWOL data";
            console.error("Error fetching AWOL data:", e);
            this.employees = [];
            this.filteredEmployees = [];
            this.stats = {
              totalEmployees: 0,
              totalAwolEmployees: 0,
              totalUnauthorizedDays: 0,
              totalUnauthorizedHours: 0
            };
          });
    },

    filterEmployees() {
      if (!Array.isArray(this.employees)) {
        this.filteredEmployees = [];
        return;
      }

      let filtered = [...this.employees];

      // Filter by search term
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(emp =>
            (emp.name && emp.name.toLowerCase().includes(searchLower)) ||
            (emp.employeeId && emp.employeeId.toLowerCase().includes(searchLower)) ||
            (emp.departmentName && emp.departmentName.toLowerCase().includes(searchLower))
        );
      }

      this.filteredEmployees = filtered;
    },

    viewEmployeeDetails(employee) {
      this.selectedEmployee = employee;
      this.employeeAbsentDates = [];
      this.loadingDetails = true;
      this.getEmployeeAbsentDates(employee.employeeId);
    },

    getEmployeeAbsentDates(employeeId) {
      const data = new FormData();
      data.append("function", "getEmployeeAbsentDates");
      data.append("employeeId", employeeId);

      if (this.selectedMonth) {
        data.append("month", this.selectedMonth);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loadingDetails = false;
            if (res.data.success) {
              this.employeeAbsentDates = Array.isArray(res.data.data.absentDates) ? res.data.data.absentDates : [];
            } else {
              this.error = res.data.message || "Failed to fetch absent dates";
              this.employeeAbsentDates = [];
            }
          })
          .catch((e) => {
            this.loadingDetails = false;
            this.error = e.message || "An error occurred while fetching absent dates";
            console.error("Error fetching absent dates:", e);
            this.employeeAbsentDates = [];
          });
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD MMM YYYY');
    },

    downloadReport() {
      try {
        if (!Array.isArray(this.employees) || this.employees.length === 0) {
          alert("No data available for download.");
          return;
        }

        const brandedRows = buildBrandedHeaderRows({
          companyName: getCompanyName(),
          reportTitle: 'AWOL (Absent Without Leave) Report',
          period: this.selectedMonthName,
          department: this.selectedDepartmentName
        });

        // Add headers
        const headers = [
          'Employee ID',
          'Employee Name',
          'Department',
          'Days Off Without Permission',
          'Last Seen'
        ];

        // Create worksheet data
        const worksheetData = [...brandedRows, headers];

        // Add data rows
        this.filteredEmployees.forEach(employee => {
          worksheetData.push([
            employee.employeeId || '',
            employee.name || '',
            employee.departmentName || '',
            employee.daysOff || 0,
            this.formatDate(employee.lastSeen)
          ]);
        });

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths
        const wscols = [
          { wch: 15 }, // Employee ID
          { wch: 25 }, // Employee Name
          { wch: 30 }, // Department
          { wch: 15 }, // Days Off
          { wch: 20 }  // Last Seen
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "AWOL Report");

        // Generate filename
        const fileName = `AWOL_Report_${moment().format('YYYY-MM-DD')}.xlsx`;

        // Write and download the file
        XLSX.writeFile(wb, fileName);

      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert('Failed to generate Excel file. Please try again.');
      }
    },

    exportToPDF() {
      if (this.filteredEmployees.length === 0) {
        alert("No data available for export.");
        return;
      }
      try {
        const columns = ['Employee ID', 'Employee Name', 'Department', 'Days Off Without Permission', 'Last Seen'];
        const rows = this.filteredEmployees.map(employee => [
          employee.employeeId || '',
          employee.name || '',
          employee.departmentName || '',
          employee.daysOff || 0,
          this.formatDate(employee.lastSeen)
        ]);

        generateBrandedPDF({
          reportTitle: 'AWOL (Absent Without Leave) Report',
          period: this.selectedMonthName,
          companyName: getCompanyName(),
          department: this.selectedDepartmentName,
          columns,
          rows,
          summary: {
            'Total Employees': this.stats.totalEmployees || 0,
            'Absent w/o Leave': this.stats.totalAwolEmployees || 0,
            'Unauthorized Days Off': this.stats.totalUnauthorizedDays || 0,
            'Unauthorized Hours': this.stats.totalUnauthorizedHours || 0
          }
        });
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Failed to generate PDF. Please try again.');
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.report-awol-content { padding: 0; }
.search-icon { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #74788d; }

.table-responsive {
  overflow-x: auto;
}

.content-minimize {
  min-height: calc(100vh - 70px);
  padding: 0 15px;
}

.mini-stats-wid {
  height: 100%;
  width: 100%;
}

.card {
  margin-bottom: 0;
}

.avatar-sm {
  flex-shrink: 0;
}

.badge.fs-6 {
  font-size: 0.9rem !important;
  padding: 0.4rem 0.6rem;
}

@media (max-width: 768px) {
  .d-flex.flex-wrap {
    justify-content: space-between;
  }
}
</style>